/**
 * Game timer — tracks elapsed time during gameplay.
 *
 * Design:
 * - Starts on the player's first move (not on deal)
 * - Pauses when the menu opens
 * - Resumes when the menu closes
 * - Stops when the game is won
 * - Resets on new game
 * - Periodically triggers a repaint so the display updates every second
 * - Persists elapsed time to localStorage so it survives page refreshes
 *
 * Uses performance.now() for sub-millisecond accuracy within a session.
 */

import { schedulePaint } from './paint'

const TIMER_STORAGE_KEY = 'solitaire-timer'

// ─── State ──────────────────────────────────────────────────────────────────

/** Accumulated elapsed time from previous sessions (ms). */
let _accumulated = 0

/** Timestamp (performance.now()) when the current running session started, or null if paused/stopped. */
let _sessionStart: number | null = null

/** Whether the timer has been started at all (first move made). */
let _started = false

/** setInterval handle for periodic repaint while running. */
let _tickInterval: ReturnType<typeof setInterval> | null = null

// ─── Core API ───────────────────────────────────────────────────────────────

/** Start the timer (called on first move). No-op if already started. */
export function timerStart(): void {
    if (_started) return
    _started = true
    _sessionStart = performance.now()
    _startTick()
    _persist()
}

/** Pause the timer (e.g. menu opened). Accumulates elapsed time. */
export function timerPause(): void {
    if (!_started || _sessionStart === null) return
    _accumulated += performance.now() - _sessionStart
    _sessionStart = null
    _stopTick()
    _persist()
}

/** Resume the timer after a pause (e.g. menu closed). */
export function timerResume(): void {
    if (!_started || _sessionStart !== null) return // not started, or already running
    _sessionStart = performance.now()
    _startTick()
}

/** Stop the timer permanently (game won). Returns final elapsed ms. */
export function timerStop(): number {
    if (_sessionStart !== null) {
        _accumulated += performance.now() - _sessionStart
        _sessionStart = null
    }
    _started = false
    _stopTick()
    _persist()
    return _accumulated
}

/** Reset the timer for a new game. */
export function timerReset(): void {
    _accumulated = 0
    _sessionStart = null
    _started = false
    _stopTick()
    _clearPersisted()
}

/** Get the current elapsed time in milliseconds. */
export function timerGetElapsed(): number {
    if (_sessionStart !== null) {
        return _accumulated + (performance.now() - _sessionStart)
    }
    return _accumulated
}

/** Whether the timer has been started (first move made this game). */
export function timerIsStarted(): boolean {
    return _started
}

// ─── Display formatting ─────────────────────────────────────────────────────

/** Format elapsed ms as "M:SS" (e.g. "2:05") or "H:MM:SS" for long games. */
export function formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// ─── Periodic repaint tick ──────────────────────────────────────────────────

function _startTick(): void {
    if (_tickInterval !== null) return
    _tickInterval = setInterval(() => {
        schedulePaint()
    }, 1000)
}

function _stopTick(): void {
    if (_tickInterval !== null) {
        clearInterval(_tickInterval)
        _tickInterval = null
    }
}

// ─── Persistence ────────────────────────────────────────────────────────────

interface TimerData {
    accumulated: number
    started: boolean
}

function _persist(): void {
    try {
        const data: TimerData = {
            accumulated: _accumulated,
            started: _started,
        }
        localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(data))
    } catch {
        // localStorage unavailable — ignore
    }
}

function _clearPersisted(): void {
    try {
        localStorage.removeItem(TIMER_STORAGE_KEY)
    } catch {
        // ignore
    }
}

/** Restore timer state from localStorage (called on page load when restoring a game). */
export function timerRestore(): void {
    try {
        const stored = localStorage.getItem(TIMER_STORAGE_KEY)
        if (stored) {
            const data: TimerData = JSON.parse(stored)
            _accumulated = data.accumulated ?? 0
            _started = data.started ?? false
            // Don't auto-resume — the timer stays paused until the player makes a move
            // or the game detects it should resume
            _sessionStart = null
        }
    } catch {
        // ignore — start fresh
    }
}

/** Resume a restored timer (called after state restore confirms game is in progress). */
export function timerResumeRestored(): void {
    if (_started && _sessionStart === null) {
        _sessionStart = performance.now()
        _startTick()
    }
}
